<template>
    <v-row class="ma-0 pb-offset">

        <!-- Header -->
        <v-col cols="12" class="pa-0 mb-4">
            <v-card class="round-card background-2">
                <v-card-title class="pa-3">
                    <v-icon color="primary" class="mr-3">mdi-clock-outline</v-icon>
                    <span>Orari di Lavoro</span>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        fab
                        small
                        @click="openCreate"
                        :disabled="loading"
                    >
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-card-title>
            </v-card>
        </v-col>

        <!-- Loading -->
        <v-col v-if="loading" cols="12" class="pa-0 text-center py-8">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </v-col>

        <!-- Empty state -->
        <v-col v-else-if="configs.length === 0" cols="12" class="pa-0">
            <v-alert type="info" text class="mb-4">
                <div class="font-weight-medium">Nessun orario di lavoro configurato</div>
                <div class="caption mt-1">Clicca su "+" per aggiungere un nuovo orario di lavoro.</div>
            </v-alert>
        </v-col>

        <!-- Config cards -->
        <template v-else>
            <v-col
                v-for="config in configs"
                :key="config.id"
                cols="12"
                class="pa-0 mb-3"
            >
                <v-card class="round-card background-2">
                    <v-card-title class="pa-3 pb-1">
                        <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                        <span class="subtitle-1 font-weight-medium">{{ config.name }}</span>
                        <v-spacer></v-spacer>
                        <v-btn icon small @click="openEdit(config)">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small @click="duplicate(config)">
                            <v-icon small>mdi-content-duplicate</v-icon>
                        </v-btn>
                        <v-btn icon small color="error" @click="confirmDelete(config)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-3 pt-1">
                        <div v-if="config.description" class="caption grey--text mb-2">{{ config.description }}</div>
                        <div class="d-flex flex-wrap">
                            <v-chip
                                v-for="day in getActiveDays(config)"
                                :key="day.label"
                                x-small
                                class="mr-1 mb-1"
                                color="primary"
                                outlined
                            >
                                {{ day.label }}: {{ day.hours }}
                            </v-chip>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </template>

        <!-- Create / Edit dialog -->
        <v-dialog v-model="dialog" max-width="900" persistent>
            <v-card class="round-card">
                <v-card-title class="pa-4">
                    <v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
                    {{ editingId ? 'Modifica Orario di Lavoro' : 'Nuovo Orario di Lavoro' }}
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text class="pa-4">
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="form.name"
                                label="Nome"
                                outlined
                                dense
                                hide-details="auto"
                                :rules="[v => !!v || 'Campo obbligatorio']"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="form.description"
                                label="Descrizione"
                                outlined
                                dense
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-tabs
                        class="tabs-pill mt-4"
                        height="34"
                        background-color="background-1"
                        slider-color="primary"
                        v-model="innerTab"
                    >
                        <v-tab ripple class="secondary-tab">Orario Settimanale</v-tab>
                        <v-tab ripple class="secondary-tab">Eccezioni</v-tab>

                        <v-tab-item class="background-1 pt-3 pb-1">
                            <working-hours-weekly-schedule
                                v-model="form.schedules"
                            ></working-hours-weekly-schedule>
                        </v-tab-item>

                        <v-tab-item class="background-1 pt-3 pb-1">
                            <working-hours-exceptions
                                v-model="form.exceptions"
                            ></working-hours-exceptions>
                        </v-tab-item>
                    </v-tabs>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions class="pa-3">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDialog" :disabled="saving">Annulla</v-btn>
                    <v-btn color="primary" @click="save" :loading="saving">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete confirmation dialog -->
        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card class="round-card">
                <v-card-title>
                    <v-icon color="error" class="mr-2">mdi-alert</v-icon>
                    Conferma Eliminazione
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    Sei sicuro di voler eliminare l'orario di lavoro
                    <strong>{{ configToDelete ? configToDelete.name : '' }}</strong>?
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">Annulla</v-btn>
                    <v-btn color="error" @click="deleteConfig" :loading="deleting">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-row>
</template>

<script>
import WorkingHoursWeeklySchedule from './WorkingHoursWeeklySchedule.vue'
import WorkingHoursExceptions from './WorkingHoursExceptions.vue'

const DEFAULT_SCHEDULES = [
    { day_of_week: 0, start_time: '09:00', end_time: '13:00' },
    { day_of_week: 0, start_time: '14:00', end_time: '18:00' },
    { day_of_week: 1, start_time: '09:00', end_time: '13:00' },
    { day_of_week: 1, start_time: '14:00', end_time: '18:00' },
    { day_of_week: 2, start_time: '09:00', end_time: '13:00' },
    { day_of_week: 2, start_time: '14:00', end_time: '18:00' },
    { day_of_week: 3, start_time: '09:00', end_time: '13:00' },
    { day_of_week: 3, start_time: '14:00', end_time: '18:00' },
    { day_of_week: 4, start_time: '09:00', end_time: '13:00' },
    { day_of_week: 4, start_time: '14:00', end_time: '18:00' },
]

const DAY_NAMES = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']

function emptyForm() {
    return {
        name: '',
        description: '',
        timezone: 'Europe/Rome',
        schedules: JSON.parse(JSON.stringify(DEFAULT_SCHEDULES)),
        exceptions: []
    }
}

export default {
    name: 'WorkingHoursConfig',

    components: {
        WorkingHoursWeeklySchedule,
        WorkingHoursExceptions
    },

    data() {
        return {
            loading: false,
            configs: [],
            dialog: false,
            innerTab: 0,
            editingId: null,
            form: emptyForm(),
            saving: false,
            deleteDialog: false,
            configToDelete: null,
            deleting: false
        }
    },

    methods: {
        loadConfigs() {
            this.loading = true
            this.axios.post('workinghours-filters', {})
                .then(response => {
                    this.configs = response.data.data || response.data || []
                })
                .catch(error => {
                    console.error('Error loading working hours configs:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Errore nel caricamento degli orari',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },

        openCreate() {
            this.editingId = null
            this.form = emptyForm()
            this.innerTab = 0
            this.dialog = true
        },

        openEdit(config) {
            this.editingId = config.id
            this.innerTab = 0
            this.form = {
                name: config.name || '',
                description: config.description || '',
                timezone: config.timezone || 'Europe/Rome',
                schedules: config.schedules ? JSON.parse(JSON.stringify(config.schedules)) : JSON.parse(JSON.stringify(DEFAULT_SCHEDULES)),
                exceptions: config.exceptions ? JSON.parse(JSON.stringify(config.exceptions)) : []
            }
            this.dialog = true
        },

        duplicate(config) {
            this.editingId = null
            this.innerTab = 0
            this.form = {
                name: (config.name || '') + ' (copia)',
                description: config.description || '',
                timezone: config.timezone || 'Europe/Rome',
                schedules: config.schedules ? JSON.parse(JSON.stringify(config.schedules)) : JSON.parse(JSON.stringify(DEFAULT_SCHEDULES)),
                exceptions: config.exceptions ? JSON.parse(JSON.stringify(config.exceptions)) : []
            }
            this.dialog = true
        },

        closeDialog() {
            this.dialog = false
            this.editingId = null
            this.form = emptyForm()
        },

        save() {
            if (!this.form.name) {
                this.$store.commit('setSnackbar', {
                    value: true,
                    text: 'Il nome è obbligatorio',
                    color: 'warning',
                    timeout: 3000
                })
                return
            }
            this.saving = true
            const payload = {
                name: this.form.name,
                description: this.form.description,
                timezone: this.form.timezone,
                schedules: this.form.schedules,
                exceptions: this.form.exceptions
            }
            const request = this.editingId
                ? this.axios.put(`workinghours/${this.editingId}`, payload)
                : this.axios.post('workinghours', payload)

            request
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: this.editingId ? 'Orario aggiornato con successo' : 'Orario creato con successo',
                        color: 'success',
                        timeout: 3000
                    })
                    this.closeDialog()
                    this.loadConfigs()
                })
                .catch(error => {
                    console.error('Error saving working hours config:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore nel salvataggio',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.saving = false
                })
        },

        confirmDelete(config) {
            this.configToDelete = config
            this.deleteDialog = true
        },

        deleteConfig() {
            if (!this.configToDelete) return
            this.deleting = true
            this.axios.post('workinghours/delete', { ids: [this.configToDelete.id] })
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Orario eliminato con successo',
                        color: 'success',
                        timeout: 3000
                    })
                    this.deleteDialog = false
                    this.configToDelete = null
                    this.loadConfigs()
                })
                .catch(error => {
                    console.error('Error deleting working hours config:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore nell\'eliminazione',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.deleting = false
                })
        },

        getActiveDays(config) {
            if (!config.schedules || config.schedules.length === 0) return []
            const byDay = {}
            config.schedules.forEach(s => {
                if (!byDay[s.day_of_week]) byDay[s.day_of_week] = []
                byDay[s.day_of_week].push(`${(s.start_time || '').substring(0, 5)}-${(s.end_time || '').substring(0, 5)}`)
            })
            return Object.keys(byDay).sort((a, b) => a - b).map(dow => ({
                label: DAY_NAMES[parseInt(dow)],
                hours: byDay[dow].join(', ')
            }))
        }
    },

    mounted() {
        this.loadConfigs()
    }
}
</script>
